import React from 'react';
import { Mail } from 'lucide-react';
import Badge from '@components/common/Badge';
import Select from '@components/common/Select';
import type { SelectOption } from '@components/common/Select';
import type { WidgetSubscription, WidgetSubscriptionSchedule } from '@app-types';
import styles from './SubscriptionFrequencySelect.module.css';

const NOT_SUBSCRIBED = 'none';

/**
 * The choices read as the schedule the server fixes: the time and the day are not selectable, only how often.
 */
const FREQUENCY_OPTIONS: SelectOption[] = [
  { value: NOT_SUBSCRIBED, label: 'Not subscribed' },
  { value: 'daily', label: 'Daily at 07:00' },
  { value: 'weekly', label: 'Weekly, Monday at 07:00' },
  { value: 'monthly', label: 'Monthly, the 1st at 07:00' }
];

interface SubscriptionFrequencySelectProps {
  widgetKey: string;
  /** The user's Widget Subscription to this widget, if any. */
  subscription: WidgetSubscription | undefined;
  disabled?: boolean;
  /** The chosen schedule, or null to turn the subscription off. */
  onChange: (schedule: WidgetSubscriptionSchedule | null) => void;
}

/**
 * Where a user chooses how often a Dashboard Widget is emailed to them: daily, weekly or monthly, or not at all.
 * Widgets of the same user on the same schedule arrive together in one email with one PDF.
 */
const SubscriptionFrequencySelect: React.FC<SubscriptionFrequencySelectProps> = ({
  widgetKey,
  subscription,
  disabled = false,
  onChange
}) => (
  <div className={styles.container} data-test-id={`widget-${widgetKey}-subscription`}>
    <Mail size={16} className={styles.icon} aria-hidden="true" />
    <Select
      label="Email me this widget"
      labelPosition="left"
      options={FREQUENCY_OPTIONS}
      value={subscription?.schedule ?? NOT_SUBSCRIBED}
      disabled={disabled}
      dataTestId={`widget-${widgetKey}-frequency`}
      onChange={(value) => onChange(value === NOT_SUBSCRIBED ? null : (value as WidgetSubscriptionSchedule))}
    />
    {subscription?.paused && (
      <Badge variant="warning" title="Your roles no longer have access to this widget. The email resumes if access is restored.">
        Paused
      </Badge>
    )}
  </div>
);

export { SubscriptionFrequencySelect };
